"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Check, Code, Copy, Sparkles, Zap } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { models, type ModelDefinition } from "@llmgateway/models";
import { getProviderIcon } from "@llmgateway/shared/components";
export type CodingModelsView = "all" | "cheap" | "flagship";
interface RecommendedModel {
  id: string;
  category: "cheap" | "flagship";
}

// Cheap models (mostly Chinese open-weights) are listed first so the default
// "all" view leads with them — we don't want to heavily promote flagship-only
// pricing on a fixed-cost DevPass plan.
const RECOMMENDED_MODELS: RecommendedModel[] = [{
  id: "glm-5.1",
  category: "cheap"
}, {
  id: "kimi-k2.6",
  category: "cheap"
}, {
  id: "qwen3-coder",
  category: "cheap"
}, {
  id: "deepseek-v4-pro",
  category: "cheap"
}, {
  id: "gpt-5.3-codex",
  category: "flagship"
}, {
  id: "claude-opus-4-7",
  category: "flagship"
}, {
  id: "gpt-5.5",
  category: "flagship"
}, {
  id: "gemini-3.1-pro-preview",
  category: "flagship"
}];
interface CodingModelsShowcaseProps {
  uiUrl: string;
  showCTA?: boolean;
  className?: string;
  showTabs?: boolean;
  defaultView?: CodingModelsView;
}
function formatContextSize(size: number): string {
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M`;
  }
  if (size >= 1000) {
    return `${(size / 1000).toFixed(0)}K`;
  }
  return size.toString();
}
function formatPrice(price: number): string {
  const perMillion = price * 1e6;
  if (perMillion < 0.01) {
    return perMillion.toFixed(4);
  }
  if (perMillion < 1) {
    return perMillion.toFixed(2);
  }
  return perMillion.toFixed(2);
}
const TAB_DEFINITIONS: {
  value: CodingModelsView;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  description: string;
}[] = [{
  value: "cheap",
  label: "Cheap",
  icon: Zap,
  description: "Frontier open-weight models — best price per token."
}, {
  value: "flagship",
  label: "Flagship",
  icon: Sparkles,
  description: "Top-tier closed models — highest capability per call."
}, {
  value: "all",
  label: "All",
  icon: Code,
  description: "Every recommended coding model, with cheap open-weights listed first."
}];
export function CodingModelsShowcase(t0) {
  const $ = _c(45);
  const {
    uiUrl,
    showCTA,
    className,
    showTabs: t1,
    defaultView: t2
  } = t0;
  const showTabs = t1 === undefined ? false : t1;
  const defaultView = t2 === undefined ? "all" : t2;
  const [copiedModel, setCopiedModel] = useState(null);
  const [view, setView] = useState(defaultView);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map(RECOMMENDED_MODELS.map(_temp));
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const idByCategory = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Map(RECOMMENDED_MODELS.map(_temp2));
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const orderIndex = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== className || $[3] !== copiedModel || $[4] !== showTabs || $[5] !== view) {
    let t10;
    if ($[11] !== view) {
      t10 = r_1 => view === "all" ? true : r_1.category === view;
      $[11] = view;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    const filteredIds = RECOMMENDED_MODELS.filter(t10).map(_temp3);
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = (a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0);
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    const recommendedModels = (models as ModelDefinition[]).filter(m => filteredIds.includes(m.id)).sort(t11);
    let t12;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = async modelId => {
        await navigator.clipboard.writeText(modelId);
        setCopiedModel(modelId);
        setTimeout(() => setCopiedModel(null), 2000);
      };
      $[14] = t12;
    } else {
      t12 = $[14];
    }
    const copyToClipboard = t12;
    let t13;
    if ($[15] !== view) {
      t13 = TAB_DEFINITIONS.find(t => t.value === view);
      $[15] = view;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    const activeTab = t13;
    t7 = `rounded-lg border p-6 ${className ?? ""}`;
    let t14;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="flex items-center gap-2"><Code className="h-5 w-5" aria-hidden="true" /><p className="font-semibold">Recommended for coding agents</p></div>;
      $[17] = t14;
    } else {
      t14 = $[17];
    }
    let t15;
    if ($[18] !== showTabs || $[19] !== view) {
      t15 = showTabs && <div className="inline-flex items-center gap-1 rounded-lg border bg-muted/30 p-1" role="tablist" aria-label="Filter coding models">{TAB_DEFINITIONS.map(tab => {
          const TabIcon = tab.icon;
          const active = view === tab.value;
          return <button key={tab.value} type="button" role="tab" aria-selected={active} onClick={() => setView(tab.value)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}><TabIcon className="h-3.5 w-3.5" />{tab.label}</button>;
        })}</div>;
      $[18] = showTabs;
      $[19] = view;
      $[20] = t15;
    } else {
      t15 = $[20];
    }
    if ($[21] !== t15) {
      t8 = <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t14}{t15}</div>;
      $[21] = t15;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    const t16 = showTabs && activeTab ? activeTab.description : "High-performance models optimized for coding tasks with tool support and prompt caching.";
    if ($[23] !== t16) {
      t9 = <p className="text-sm text-muted-foreground mb-4">{t16}</p>;
      $[23] = t16;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t5 = "grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    let t17;
    if ($[25] !== copiedModel || $[26] !== view) {
      t17 = model => {
        const provider = model.providers[0];
        const ProviderIcon = provider ? getProviderIcon(provider.providerId) : null;
        const category = idByCategory.get(model.id);
        return <div key={model.id} className="group relative flex flex-col gap-2 rounded-lg border p-4 transition-all hover:border-primary/50 hover:shadow-sm">{view === "all" && category === "cheap" ? <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400"><Zap className="h-2.5 w-2.5" />Cheap</span> : null}<div className="flex items-start justify-between gap-2"><div className="flex-1 min-w-0"><div className="flex items-center gap-2">{ProviderIcon && <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted"><ProviderIcon className="h-4 w-4" /></div>}<span className="font-medium text-sm truncate">{model.name ?? model.id}</span></div></div></div><div className="flex items-center gap-2 mt-1"><code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model.id}</code><button type="button" onClick={() => copyToClipboard(model.id)} className="shrink-0 p-1 rounded hover:bg-muted transition-colors" title="Copy model ID">{copiedModel === model.id ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3 text-muted-foreground" />}</button></div>{provider && <div className="text-xs text-muted-foreground space-y-1 mt-1">{provider.contextSize && <p>Context:{" "}<span className="font-mono font-medium text-foreground">{formatContextSize(provider.contextSize)}</span></p>}{(provider.inputPrice !== undefined || provider.outputPrice !== undefined) && <p>{provider.inputPrice !== undefined && <><span className="font-mono font-medium text-foreground">${formatPrice(Number(provider.inputPrice))}</span><span className="text-muted-foreground"> in</span></>}{provider.outputPrice !== undefined && <><span className="text-muted-foreground mx-1">/</span><span className="font-mono font-medium text-foreground">${formatPrice(Number(provider.outputPrice))}</span><span className="text-muted-foreground"> out</span></>}<span className="text-muted-foreground/70 text-[10px] ml-1">/M tokens</span></p>}</div>}</div>;
      };
      $[25] = copiedModel;
      $[26] = view;
      $[27] = t17;
    } else {
      t17 = $[27];
    }
    t6 = recommendedModels.map(t17);
    $[2] = className;
    $[3] = copiedModel;
    $[4] = showTabs;
    $[5] = view;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  let t10;
  if ($[28] !== t5 || $[29] !== t6) {
    t10 = <div className={t5}>{t6}</div>;
    $[28] = t5;
    $[29] = t6;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = `${uiUrl}/models?category=code`;
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArrowRight className="h-3 w-3" />;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11) {
    t13 = <a href={t11} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">View all coding models{t12}</a>;
    $[32] = t11;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== showCTA) {
    t14 = showCTA && <Button asChild={true}><Link href="/signup">Get Started</Link></Button>;
    $[34] = showCTA;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <div className="mt-4 flex items-center justify-between">{t13}{t14}</div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t10 || $[40] !== t15 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t16 = <div className={t7}>{t8}{t9}{t10}{t15}</div>;
    $[39] = t10;
    $[40] = t15;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
function _temp3(r_2) {
  return r_2.id;
}
function _temp2(r_0, i) {
  return [r_0.id, i] as const;
}
function _temp(r) {
  return [r.id, r.category] as const;
}